import {createSelector} from 'reselect';
import {getListingPage, getContactOwnerForm, getTranslations, getTripData} from './store';
import {getLocation} from './searchBox';
import {
    getIsMobile,
    getSiteName,
    getLocale
} from './system';
import {InquiryStates} from '../records/listingPageRecord';
import getPriceStatus from './priceStatus';
import {labelMaker} from '../../server/utils/labelMaker';
import {getLocaleConsolidatedDateString} from '../utils/dateLocaleUtils';
import {toURL} from '@homeaway/search-url-parser';
import {getBookingType, getHostType} from './listing';
import {getGuestsError} from './tripGuests';

const getInquiryButtonState = createSelector(
    [getContactOwnerForm],
    (contactOwnerForm) => {
        const isDisabled = contactOwnerForm.invalid.size > 0;
        return isDisabled;
    }
);

const getShowEnterGuestAlert = createSelector(
    [getContactOwnerForm],
    (contactOwnerForm) => {
        return !!(contactOwnerForm.invalid.has('guests'));
    }
);

/* eslint-disable complexity */
const getServiceErrorLabels = createSelector(
    [getTranslations],
    (translations) => {
        // Full list of possible errors:
        // https://github.homeawaycorp.com/tx/property-details-service/blob/342245ace555c277d36024721fbe8b7b5e26360c/api/src/main/java/com/homeaway/traveler/propertydetails/model/InquiryRequest.java
        // search for "message ="
        const labelOverrides = {
            '#adults must be >=': translations.minNumAdults,
            '#adults must be <=': translations.maxNumAdults,
            '#children must be >=': translations.minNumChildren,
            '#children must be <=': translations.maxNumChildren,
            'Arrival date must be in the future': translations.arrivalDateMustBeFuture,
            'Departure date must be in the future': translations.departureDateMustBeFuture,
            'Generic error': translations.msgSendFailure
        };

        return labelOverrides;
    }
);

const getServiceErrorsArr = createSelector(
    [getListingPage, getServiceErrorLabels],
    (listingPage, serviceErrorLabels) => {
        const {inquiryServiceErrors} = listingPage;
        const hasErrors = inquiryServiceErrors.size > 0;
        const errorsArr = [];

        if (hasErrors) {
            const errorsObj = inquiryServiceErrors.toJS();
            Object.getOwnPropertyNames(errorsObj).forEach(
                (error) => {
                    // Strip out the numbers when comparing the strings for min/max guests in case these values change in the future
                    // Store whatever number is returned from the service and use it to populate the lingua key
                    const errorText = errorsObj[error];
                    const errorTextBase = errorText.split('=')[1] ? `${errorText.split('=')[0]}=` : errorText;
                    const errorTextSuffix = errorText.split('=')[1] ? (errorText.split('=')[1]).substr(1).trim() : '';

                    // If override text exists, use it. Otherwise, use the error text returned from the service.
                    const errorString = serviceErrorLabels.hasOwnProperty(errorTextBase) ? labelMaker(serviceErrorLabels[errorTextBase], errorTextSuffix) : errorText;
                    errorsArr.push(errorString);
                }
            );
        }

        return errorsArr;
    }
);

const getInquirySuccessState = createSelector(
    [getLocation, getTripData, getTranslations, getIsMobile, getSiteName, getLocale, getHostType, getContactOwnerForm],
    (location, tripData, translations, isMobile, siteName, locale, hostType, contactOwnerForm) => {
        return {
            dateString: getLocaleConsolidatedDateString(tripData.arriveDateText, tripData.departDateText, locale),
            isMobile,
            location,
            countryName: contactOwnerForm.countryName,
            phoneNumber: contactOwnerForm.phoneNumber,
            siteName,
            totalGuests: tripData.adults + tripData.children,
            labels: {
                countryCodes: translations.countryCodes,
                guests: translations.guests,
                msgSendSuccess: translations.msgSendSuccess,
                continueSearch: translations.continueSearch,
                downloadText: translations.appDownloadButtonText,
                header: hostType.owner ? translations.appDownloadHeaderOwner : translations.appDownloadHeaderManager,
                message1: isMobile ? '' : translations.appDownloadMessage,
                message2: isMobile ? translations.appDownloadMessage : translations.appDownloadSmsMessage,
                message3: translations.appDownloadAvailableOS
            },
            searchUrl: toURL({
                arrival: tripData.arriveDateText,
                departure: tripData.departDateText,
                q: encodeURIComponent(location),
                adultsCount: tripData.adults,
                childrenCount: tripData.children,
                petIncluded: tripData.hasPets
            }),
            smsWidgetTranslations: {
                cta: translations.sharedSmsWidgetCta,
                failure: translations.sharedSmsWidgetFailure,
                phone_number_label: translations.sharedSmsWidgetPhone_number_label,
                submit: translations.sharedSmsWidgetSubmit,
                submit_android: translations.sharedSmsWidgetSubmit_android,
                submit_ios: translations.sharedSmsWidgetSubmit_ios,
                success: translations.sharedSmsWidgetSuccess
            }
        };
    }
);

const getInquiryState = createSelector(
    [getBookingType, getListingPage, getContactOwnerForm, getPriceStatus, getInquiryButtonState, getServiceErrorsArr, getShowEnterGuestAlert, getTranslations, getInquirySuccessState, getGuestsError],
    (bookingType, listingPage, contactOwnerForm, priceStatus, buttonState, serviceErrorsArr, showEnterGuestsAlert, labels, inquirySuccessState, guestsError) => {
        return {
            editing: listingPage.inquiryState !== InquiryStates.MSG_SEND_SUCCESS,
            sendSuccess: listingPage.inquiryState === InquiryStates.MSG_SEND_SUCCESS,
            sendFailure: listingPage.inquiryState === InquiryStates.MSG_SEND_FAILURE,
            showAvailableNotice: !!(priceStatus.isAvailable && bookingType.onlineBookable && !guestsError),
            showEnterGuestsAlert,
            availableButtonText: (bookingType.instantBook ? labels.propertyBookNow : labels.propertyRequestToBook),
            submitDisabled: listingPage.inquiryState === InquiryStates.MSG_POSTING,
            serviceErrorsArr,
            inquirySuccessState
        };
    }
);

export default getInquiryState;

export {
    getInquiryState,
    getInquirySuccessState,
    getInquiryButtonState,
    getServiceErrorLabels,
    getServiceErrorsArr,
    getShowEnterGuestAlert
};
